import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { StyleSheet, Text, View } from './components/base_components';
import Breadcrumb from './components/breadcrumb';
import { BusyOverlay, useBusy } from './components/busy_component';
import ProgressSpinner from './components/progress_spinner';
import TextButton from './components/buttons/text_button';
import StandardScreen from './components/standard_screen';
import TitleActionLine from './components/title_action_line';
import StandardBoxList from './components/standard_box_list';
import BoxPropList from './components/box_prop_list';
import VolumeSelect from './components/volume_select';

import { useUser, useOrg } from './stores/user_store';
import UserAdminStore from './stores/user_admin_store';

const styles = StyleSheet.create({
  userListScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  dialog: {
    marginLeft: '2rem',
    marginBottom: '2rem',
    flexDirection: 'column',
  },
  empty: {
    margin: '2rem',
    color: '#555',
    fontSize: '1.5rem',
    fontWeight: '600',
  },
  spinner: {
    margin: '2rem',
  },
  text: {
    marginTop: '1em',
    fontSize: '1.5em',
  },
  input: {
    alignSelf: 'stretch',
    maxWidth: '40rem',
    marginTop: '1rem',
  },
  button: {
    marginTop: '1rem',
    alignSelf: 'flex-start',
  },
  actionButton: {
    marginLeft: '1rem',
  },
  spacer: {
    flex: 1,
  },
});

export default function UserEditScreen(props) {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const params = useParams();
  const org = useOrg();
  const user_id = parseInt(params?.user_id ?? '0', 10) || 0;
  const user = UserAdminStore.useUser(org?.org_id, user_id);
  const is_me = useUser()?.user_id === user_id;
  const [selected_volumes, setSelectedVolumes] = React.useState<number[]>([]);
  const [add_volume, setAddVolume] = React.useState(0);
  const [updates, setUpdates] = React.useState<any>({});
  const navigate = useNavigate();

  React.useEffect(() => {
    UserAdminStore.fetch(org?.org_id);
  }, [org?.org_id]);

  let volumes: any = null;
  if (!user) {
    volumes = <ProgressSpinner style={styles.spinner} color='#999' />;
  } else if (!user?.volume_list?.length) {
    volumes = <Text style={styles.empty}>No Volumes</Text>;
  }

  function _onDeletePress() {
    const volume_list = selected_volumes
      .map((index) => user?.volume_list?.[index])
      .filter((v) => v);
    if (volume_list.length > 0 && setBusy()) {
      const opts = { org_id: org?.org_id, user_id, volume_list };
      UserAdminStore.deleteVolumeList(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to delete volume, please try again.');
        } else {
          setSelectedVolumes([]);
        }
      });
    }
  }
  function _onAddPress() {
    if (add_volume && setBusy()) {
      const opts = { org_id: org?.org_id, user_id, volume_id: add_volume };
      UserAdminStore.addVolume(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to add volume, please try again.');
        } else {
          setAddVolume(0);
        }
      });
    }
  }
  function _onSave(complete) {
    if (setBusy()) {
      const opts: any = {
        org_id: org?.org_id,
        user_id,
      };
      if (updates.is_admin !== undefined) {
        opts.is_admin = updates.is_admin === 'Admin';
      }
      if (updates.cap_add_device !== undefined) {
        opts.cap_add_device = updates.cap_add_device === 'Enabled';
      }
      UserAdminStore.updateUser(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to update user.');
        } else {
          complete?.();
        }
      });
    }
  }
  function _onRemovePress() {
    const confirmed = window.confirm(
      'Are you sure you want to remove this user?'
    );
    if (confirmed && setBusy()) {
      UserAdminStore.removeUserOrg({ org_id: org?.org_id, user_id }, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to remove user.');
        } else {
          navigate(-1);
        }
      });
    }
  }

  const volume_buttons = (
    <TextButton
      style={styles.actionButton}
      text='Delete'
      disabled={selected_volumes.length === 0}
      onPress={_onDeletePress}
    />
  );
  const detail_buttons = (
    <>
      <TextButton
        style={styles.actionButton}
        type='danger'
        text='Remove User'
        onPress={_onRemovePress}
      />
      <View style={styles.spacer} />
    </>
  );
  const title = user?.user_name ?? user?.email;
  return (
    <View style={styles.userListScreen}>
      <StandardScreen>
        <Breadcrumb list={[{ text: 'Users', url: '/admin/user' }, title]} />
        <TitleActionLine title={title} />
        <BoxPropList
          title='User Details'
          editButtons={detail_buttons}
          editable={!is_me}
          updates={updates}
          onChange={setUpdates}
          onSave={_onSave}
          columns={[
            [
              {
                label: 'Email',
                value: user?.email,
              },
              {
                label: 'User Name',
                value: user?.user_name ?? '-',
              },
            ],
            [
              {
                key: 'is_admin',
                label: 'Roles',
                value: user?.is_admin ? 'Admin' : 'Member',
                radio: {
                  options: ['Admin', 'Member'],
                },
              },
              {
                key: 'cap_add_device',
                label: 'Device Add Permission',
                value: user?.cap_add_device ? 'Enabled' : 'Disabled',
                radio: {
                  options: ['Enabled', 'Disabled'],
                  disabled: user?.is_admin,
                },
              },
            ],
          ]}
        />
        <StandardBoxList
          title='Volumes'
          detail={user?.volume_list?.length}
          headers={['Volume Name']}
          buttons={volume_buttons}
          selected={selected_volumes}
          onChange={setSelectedVolumes}
          rows={user?.volume_list?.map?.((volume: any) => ({
            list: [volume?.volume_name],
          }))}
        >
          {volumes}
        </StandardBoxList>
        <StandardBoxList title='Add Volume'>
          <View style={styles.dialog}>
            <Text style={styles.text}>Add a volume to this user.</Text>
            <VolumeSelect
              style={styles.input}
              placeholder='Pick Volume'
              onlyOrgId={org?.org_id}
              excludeList={user?.volume_list}
              value={add_volume}
              onChange={setAddVolume}
            />
            <TextButton
              style={styles.button}
              text='Add Volume'
              onPress={_onAddPress}
            />
          </View>
        </StandardBoxList>
      </StandardScreen>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
